<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import BrowseLayout from '@/browse/BrowseLayout.vue'
import FilterPanel from '@/browse/FilterPanel.vue'
import FilterSection from '@/browse/FilterSection.vue'
import SelectFacet from '@/browse/SelectFacet.vue'
import GradeFacet from '@/browse/GradeFacet.vue'
import RadioFacet from '@/browse/RadioFacet.vue'
import ActiveFilters from '@/browse/ActiveFilters.vue'
import ResultStatus from '@/browse/ResultStatus.vue'
import { facetOptions } from '@/browse/browse_state'
import type { Chip, ChipRow } from '@/browse/browse_state'
import { GRADES, gradeSources } from '@/browse/grades'

const SUBJECTS = ['Reading', 'Math', 'Writing']
const CATEGORIES = ['Fluency', 'Phonics & Decoding', 'Vocabulary']
const GRADE_COUNTS = Object.fromEntries(GRADES.map((grade, index) => [grade, index % 4]))

const subject = ref<string[]>(['Reading'])
const grades = ref<string[]>(['3'])
const category = ref<string[]>([])

const filtersByKey: Record<string, Ref<string[]>> = { subject, category, grades }

function withValueToggled(values: string[], value: string): string[] {
  const isValueSelected = values.includes(value)
  if (isValueSelected) {
    return values.filter((selectedValue) => selectedValue !== value)
  }
  return [...values, value]
}

function selectSubject(value: string) {
  const isCleared = value === ''
  if (isCleared) {
    subject.value = []
    return
  }
  subject.value = [value]
}

function toggleGrade(grade: string) {
  grades.value = withValueToggled(grades.value, grade)
}

function toggleCategory(value: string) {
  const isCategorySelected = category.value.includes(value)
  if (isCategorySelected) {
    category.value = []
    return
  }
  category.value = [value]
}

function chipsForFilters(keys: string[]): Chip[] {
  return keys.flatMap((key) => filtersByKey[key].value.map((value) => ({ key, value })))
}

const chipRows = computed<ChipRow[]>(() => [
  { label: 'Subject', chips: chipsForFilters(['subject', 'category']) },
  { label: 'Grades', chips: chipsForFilters(['grades']) }
])

function removeChip(key: string, value: string) {
  const filter = filtersByKey[key]
  filter.value = filter.value.filter((selectedValue) => selectedValue !== value)
}
</script>

<template>
  <div class="space-y-8">
    <p class="text-text-md text-gray-700">
      Shared by every Algolia browse page from <code>src/browse/</code>. On a page each control is driven by
      <code>useBrowseSearch</code>, so the route query holds the filters; here they hold local state, and grade
      options with a zero count are disabled to show the unavailable state.
    </p>

    <BrowseLayout title="BrowseLayout" description="Feature header, a filter sidebar and a results region.">
      <template #filters>
        <FilterPanel>
          <FilterSection v-slot="{ labelledBy }" heading="SelectFacet">
            <SelectFacet
              :options="facetOptions(SUBJECTS, undefined, subject)"
              :labelled-by="labelledBy"
              empty-text="All subjects"
              @select="selectSubject"
            />
          </FilterSection>
          <FilterSection v-slot="{ labelledBy }" heading="GradeFacet">
            <GradeFacet
              :options="facetOptions(gradeSources(), GRADE_COUNTS, grades)"
              :labelled-by="labelledBy"
              @toggle="toggleGrade"
            />
          </FilterSection>
          <FilterSection v-slot="{ labelledBy }" heading="RadioFacet">
            <RadioFacet
              :options="facetOptions(CATEGORIES, undefined, category)"
              :labelled-by="labelledBy"
              @toggle="toggleCategory"
            />
          </FilterSection>
          <FilterSection heading="FilterSection, unavailable" unavailable-text="Choose a subject first" />
        </FilterPanel>
      </template>

      <ActiveFilters :rows="chipRows" @remove="removeChip" />
      <ResultStatus
        :has-criteria="true"
        :is-loading="false"
        :has-failed="false"
        :shown-count="12"
        :total-count="12"
        no-results-text="No results match these filters."
      />
    </BrowseLayout>
  </div>
</template>
